// src/Components/notifications/NotificationBell.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { notificationAPI } from '../../Components/services/api';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 15 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (isOpen) {
        fetchNotifications();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Add event listener for notification-read events
  useEffect(() => {
    const handleNotificationRead = () => {
      console.log('🔔 Notification read event received, refreshing...');
      fetchUnreadCount();
      if (isOpen) {
        fetchNotifications();
      }
    };
    
    window.addEventListener('notification-read', handleNotificationRead);
    return () => window.removeEventListener('notification-read', handleNotificationRead);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      console.log('📊 Unread count response:', response.data);
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getAll(0, 20);
      console.log('📥 Notifications response:', response.data);
      setNotifications(response.data.data.notifications || []);
      setUnreadCount(response.data.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      console.log('📖 Marking notification as read:', notificationId);
      const response = await notificationAPI.markAsRead(notificationId);
      console.log('📥 Mark as read response:', response.data);
      
      if (response.data && response.data.success) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast.success('Marked as read');
        
        // Dispatch event to update other components
        window.dispatchEvent(new Event('notification-read'));
      } else {
        toast.error(response.data?.message || 'Failed to mark as read');
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
      toast.error(error.response?.data?.message || 'Failed to mark as read');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (actionLoading) return;
    if (unreadCount === 0) {
      toast.info('No unread notifications');
      return;
    }
    
    setActionLoading(true);
    try {
      console.log('📖 Marking all notifications as read...');
      const response = await notificationAPI.markAllAsRead();
      console.log('📥 Mark all as read response:', response.data);
      
      if (response.data && response.data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
        toast.success('All notifications marked as read');
        
        // Dispatch event to update other components
        window.dispatchEvent(new Event('notification-read'));
      } else {
        toast.error(response.data?.message || 'Failed to mark all as read');
      }
    } catch (error) {
      console.error('❌ Error marking all as read:', error);
      toast.error(error.response?.data?.message || 'Failed to mark all as read');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      console.log('🗑️ Deleting notification:', notificationId);
      const response = await notificationAPI.delete(notificationId);
      console.log('📥 Delete response:', response.data);
      
      if (response.data && response.data.success) {
        const victim = notifications.find(n => n.id === notificationId);
        if (victim && !victim.isRead) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        window.dispatchEvent(new Event('notification-read'));
        toast.success('Notification deleted');
      } else {
        toast.error(response.data?.message || 'Failed to delete notification');
      }
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) return;
    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      console.log('🗑️ Deleting all notifications...');
      const response = await notificationAPI.deleteAll();
      console.log('📥 Delete all response:', response.data);
      
      if (response.data && response.data.success) {
        setNotifications([]);
        setUnreadCount(0);
        window.dispatchEvent(new Event('notification-read'));
        toast.success('All notifications deleted');
      } else {
        toast.error(response.data?.message || 'Failed to delete all notifications');
      }
    } catch (error) {
      console.error('❌ Error deleting all notifications:', error);
      toast.error(error.response?.data?.message || 'Failed to delete all notifications');
    } finally {
      setActionLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'SUBSCRIPTION':
        return '📦';
      case 'USER':
        return '👤';
      case 'PAYMENT':
        return '💰';
      case 'SYSTEM':
        return '⚙️';
      default:
        return '🔔';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Notifications"
        disabled={actionLoading}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  fetchNotifications();
                  toast.info('Refreshed');
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
                disabled={actionLoading}
              >
                🔄
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  disabled={actionLoading}
                >
                  {actionLoading ? '...' : 'Mark all read'}
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                  disabled={actionLoading}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg mb-1">📭</p>
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (!actionLoading) {
                      setIsOpen(false);
                      navigate(`/notifications/${notification.id}`);
                    }
                  }}
                  className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.isRead ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 text-xl">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p className={`text-sm font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </p>
                        <div className="flex items-center space-x-2 ml-2">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => handleMarkAsRead(notification.id, e)}
                              className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                              disabled={actionLoading}
                            >
                              Mark read
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(notification.id, e)}
                            className="text-xs text-gray-400 hover:text-red-600"
                            disabled={actionLoading}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2 whitespace-pre-line">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimeAgo(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                disabled={actionLoading}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;